<template>
    <div class="relative country-selector-wrapper">
        <!-- Floating dispatch banner -->
        <transition name="slide-down">
            <!-- Mobile version with backdrop and centered dialog -->
            <div
                v-if="selected.title && showBanner && !show"
                class="fixed lg:absolute inset-0 lg:inset-auto lg:top-full lg:left-0 flex items-center justify-center lg:items-start lg:justify-start lg:mt-[16px] z-[1200] lg:w-[360px] tooltip-bubble"
            >
                <!-- Backdrop for mobile only -->
                <div
                    class="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
                ></div>

                <div
                    class="w-[360px] max-w-[90%] lg:max-w-none lg:w-full flex flex-col bg-white shadow-[0px_4px_25.3px_0px_rgba(0,0,0,0.12)] px-[16px] py-[18px] gap-[18px] rounded-[8px] relative z-10"
                >
                    <!-- Triangle tooltip pointer (desktop only) -->
                    <div
                        class="hidden lg:block absolute -top-[10px] left-[20px] w-[36px] h-[10px] overflow-hidden"
                    >
                        <div
                            class="w-[24px] h-[24px] bg-white rotate-45 transform origin-bottom-left shadow-[0px_4px_25.3px_0px_rgba(0,0,0,0.12)] absolute -bottom-[7px] left-[2px]"
                        ></div>
                    </div>

                    <div class="text-center text-[14px]">
                        {{ in18["cs_dispatch_banner_line1"] }}
                        <strong>{{ selected.title }}</strong
                        >.
                        {{ in18["cs_dispatch_banner_line2"] }}
                    </div>

                    <div class="flex gap-[16px] w-full">
                        <button
                            @click="closeBanner"
                            class="flex-1 bg-[#F5F5FD] hover:bg-[#F7F7FC] text-[#252525] rounded-[8px] px-[12px] py-[8px] text-[14px] font-semibold transition-all"
                        >
                            {{ in18["cs_continue"] }}
                        </button>
                        <button
                            @click="handleChangeAddress"
                            class="flex-1 bg-[#273276] hover:bg-opacity-80 text-white rounded-[8px] px-[12px] py-[8px] text-[14px] font-semibold transition-all"
                        >
                            {{ in18["cs_change_address"] }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Country selector trigger + modal -->
        <div class="">
            <button
                v-show="selected.title"
                type="button"
                @click="open"
                class="hidden lg:flex h-[44px] overflow-hidden group min-w-[200px] items-center gap-[8px] cursor-pointer pl-[12px] pr-[14px] text-[#252525] bg-[#FAFAFA] hover:bg-[#F7F7FC] hover:border-transparent border border-[#EFEEEE] rounded-[6px]"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                >
                    <path
                        d="M7.99992 8.95346C9.14867 8.95346 10.0799 8.02221 10.0799 6.87346C10.0799 5.72471 9.14867 4.79346 7.99992 4.79346C6.85117 4.79346 5.91992 5.72471 5.91992 6.87346C5.91992 8.02221 6.85117 8.95346 7.99992 8.95346Z"
                        stroke="#252525"
                    />
                    <path
                        d="M2.41345 5.66016C3.72679 -0.113169 12.2801 -0.106503 13.5868 5.66683C14.3535 9.0535 12.2468 11.9202 10.4001 13.6935C9.06012 14.9868 6.94012 14.9868 5.59345 13.6935C3.75345 11.9202 1.64679 9.04683 2.41345 5.66016Z"
                        stroke="#252525"
                    />
                </svg>
                <div class="h-[44px] relative w-full overflow-hidden">
                    <div
                        class="h-[44px] items-start justify-center flex flex-col absolute top-0 left-0 w-full text-[12px] transition-transform duration-300 transform group-hover:-translate-y-full"
                    >
                        <span>{{ in18["cs_deliver_to"] }}</span>
                        <div class="w-full text-left truncate">
                            <span class="font-semibold">{{
                                selected.title
                            }}</span>
                        </div>
                    </div>
                    <div
                        class="h-[44px] flex items-center absolute top-full left-0 w-full text-[12px] transition-transform duration-300 transform group-hover:-translate-y-full"
                    >
                        <span class="text-left">{{ in18["cs_change_address"] }}</span>
                    </div>
                </div>
            </button>

            <!-- Modal overlay -->
            <transition name="fade">
                <div v-if="show" class="overlay" @click.self="close">
                    <div
                        class="md:max-w-[426px] max-w-[100%] bg-white rounded-[15px]"
                    >
                        <div class="flex flex-col p-[20px] gap-[16px]">
                            <div class="flex justify-between items-center">
                                <h5
                                    class="text-[18px] font-medium leading-[32px] text-[#252525]"
                                >
                                    {{ in18["cs_choose_your_location"] }}
                                </h5>
                                <button
                                    type="button"
                                    class="w-[24px] h-[24px] rounded-[6px] flex items-center justify-center bg-[#F7F7FC] hover:bg-[#273276] transition-all text-[#606060] hover:text-white"
                                    :aria-label="in18['cs_close']"
                                    @click="close"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                    >
                                        <path
                                            d="M7.09938 6.49997L11.7719 1.82726C12.076 1.52332 12.076 1.0319 11.7719 0.727955C11.468 0.424015 10.9766 0.424015 10.6726 0.727955L5.99993 5.40066L1.32737 0.727955C1.02329 0.424015 0.532002 0.424015 0.228062 0.727955C-0.0760206 1.0319 -0.0760206 1.52332 0.228062 1.82726L4.90062 6.49997L0.228062 11.1727C-0.0760206 11.4766 -0.0760206 11.968 0.228062 12.272C0.379534 12.4236 0.578696 12.4998 0.777716 12.4998C0.976737 12.4998 1.17576 12.4236 1.32737 12.272L5.99993 7.59928L10.6726 12.272C10.8243 12.4236 11.0233 12.4998 11.2223 12.4998C11.4213 12.4998 11.6203 12.4236 11.7719 12.272C12.076 11.968 12.076 11.4766 11.7719 11.1727L7.09938 6.49997Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div
                                class="px-[16px] pt-[12px] pb-[18px] flex flex-col gap-[16px] bg-[#F7F7FC] rounded-[8px]"
                            >
                                <p class="text-[14px] text-center">
                                    {{ in18["cs_delivery_options_note"] }}
                                </p>

                                <a
                                    v-if="!signedIn"
                                    role="button"
                                    class="block text-white truncate bg-[#273276] hover:bg-opacity-80 rounded-[8px] text-center transition-colors duration-200 w-full text-[14px] font-semibold px-[24px] py-[10px]"
                                    :href="loginUrl"
                                >
                                    {{ in18["cs_sign_in_to_see_addresses"] }}
                                </a>
                            </div>

                            <div
                                class="px-[16px] mt-[8px] pt-[12px] pb-[18px] flex flex-col gap-[16px] border border-[#E2E2EF] rounded-[8px] relative"
                            >
                                <div
                                    class="absolute -top-[11px] left-1/2 transform -translate-x-1/2 bg-white px-[16px]"
                                >
                                    <span
                                        class="text-[14px] text-[#606060] whitespace-nowrap"
                                    >
                                        {{ in18["cs_or_deliver_outside"] }}
                                        {{ in18["cs_or_deliver_country"] }}
                                    </span>
                                </div>

                                <div
                                    v-if="loading"
                                    class="my-4 d-flex justify-content-center"
                                >
                                    <span
                                        class="spinner-border"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                </div>

                                <Multiselect
                                    v-else
                                    :key="countryMultiselectKey"
                                    ref="countryMultiselect"
                                    v-model="selected"
                                    :allow-empty="false"
                                    :custom-label="(country) => country.title"
                                    :disabled="loading"
                                    :internal-search="true"
                                    :options="countries"
                                    :placeholder="in18['cs_search_placeholder']"
                                    :preserve-search="true"
                                    :searchable="true"
                                    :show-labels="false"
                                    class="relative border-b border-gray-300 outline-none country-multiselect"
                                    label="title"
                                    track-by="id"
                                    @blur="handleMultiselectBlur"
                                    @focus="handleMultiselectFocus"
                                    @select="handleSelect"
                                    @search-change="onSearchChange"
                                >
                                    <template #singleLabel="{ option }">
                                        <div
                                            v-if="option"
                                            class="flex justify-between items-center px-2 py-[18px] w-full pointer-events-none"
                                        >
                                            <div
                                                class="flex gap-2 items-center pl-2"
                                            >
                                                <img
                                                    v-if="option.code"
                                                    :alt="option.code"
                                                    :src="`/shop/images/flags/${option.code.toLowerCase()}.svg`"
                                                    class="mr-2 w-7 h-5"
                                                />
                                                <span
                                                    class="mr-2 text-[16px] font-medium leading-none"
                                                    >{{ option.title }}</span
                                                >
                                            </div>
                                        </div>
                                        <div
                                            v-else
                                            class="flex justify-between items-center px-2 py-[18px] w-full pointer-events-none"
                                        >
                                            {{
                                                in18[
                                                    "cs_attribute_shipping_country_option"
                                                ]
                                            }}
                                        </div>
                                    </template>
                                    <template #option="{ option }">
                                        <div class="flex gap-2 items-center">
                                            <img
                                                :alt="option.code"
                                                :src="`/shop/images/flags/${option.code.toLowerCase()}.svg`"
                                                class="mr-2 w-7 h-5"
                                            />
                                            <span>{{ option.title }}</span>
                                        </div>
                                    </template>
                                    <template #noResult>
                                        <div class="px-3 py-2 text-gray-600">
                                            {{ in18["cs_no_results_found"] }}
                                        </div>
                                    </template>
                                </Multiselect>
                                <button
                                    type="button"
                                    class="block text-white font-medium truncate leading-tight bg-[#23B14D] hover:bg-opacity-80 rounded-[5px] text-center transition-colors duration-200 border border-transparent w-full text-[1.4rem] px-8 py-[1rem]"
                                    :class="{
                                        'opacity-50 cursor-default': !selected,
                                    }"
                                    @click="save"
                                    :disabled="!selected"
                                >
                                    {{ in18["cs_done"] }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";

export default {
    name: "CountrySelector",
    components: { Multiselect },
    props: {
        in18: { type: Object, required: true },
        loginUrl: { type: String, required: true },
        initialCode: { type: String, required: true },
        signedIn: { type: Boolean, default: false },
    },
    data() {
        return {
            countries: [],
            selected: { code: this.initialCode, title: "" },
            show: false,
            showBanner: localStorage.getItem("dispatchBannerDismissed") !== "true",
            loading: false,
            countryMultiselectKey: 0,
        };
    },
    mounted() {
        this.fetchCountries();
    },
    methods: {
        open() {
            this.show = true;
            if (!this.countries.length) {
                this.fetchCountries();
            } else {
                this.selected = this.countries.find(
                    (c) => c.code === this.selected.code
                );
            }
        },
        close() {
            this.show = false;
        },
        save() {
            if (!this.selected) return;
            axios
                .get("/api/user/country/" + this.selected.id)
                .then(() => window.location.reload())
                .catch((error) => {
                    window.toastr.error(
                        error.response?.data?.message ||
                            this.in18["cs_error_update_country"]
                    );
                });
            this.show = false;
        },
        fetchCountries() {
            this.loading = true;
            axios
                .get("/api/shipping/countries")
                .then((r) => {
                    this.countries = r.data.countries;
                    const found = this.countries.find(
                        (c) => c.code === this.initialCode
                    );
                    if (found) this.selected = found;
                })
                .catch(console.error)
                .finally(() => (this.loading = false));
        },
        flagUrl(code) {
            return `/shop/images/flags/${code.toLowerCase()}.svg`;
        },
        handleSelect() {
            setTimeout(() => {
                if (this.$refs.countryMultiselect) {
                    this.countryMultiselectKey++;
                    this.$refs.countryMultiselect.isOpen = false;
                    this.keepDropdownOpen = false;
                    document.activeElement?.blur();
                }
            }, 50);
        },
        onSearchChange(query) {
            this.keepDropdownOpen = true;
            if (
                this.$refs.countryMultiselect &&
                !this.$refs.countryMultiselect.isOpen
            ) {
                this.$nextTick(() => this.$refs.countryMultiselect.activate());
            }
        },
        handleMultiselectBlur() {
            setTimeout(() => {
                if (!this.$el.contains(document.activeElement)) {
                    this.keepDropdownOpen = false;
                }
            }, 100);
        },
        handleMultiselectFocus() {
            this.keepDropdownOpen = true;
            if (this.$refs.countryMultiselect) {
                this.$nextTick(() => {
                    if (!this.$refs.countryMultiselect.isOpen) {
                        this.$refs.countryMultiselect.activate();
                    }
                });
            }
        },
        closeBanner() {
            this.showBanner = false;
            localStorage.setItem("dispatchBannerDismissed", "true");
        },
        handleChangeAddress() {
            this.closeBanner();
            this.open();
        },
    },
};
</script>

<style scoped>
.country-selector-wrapper {
    position: relative;
}

.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.2s ease-in;
}

/* For mobile: fade transition */
@media (max-width: 1023px) {
    .slide-down-enter,
    .slide-down-leave-to {
        opacity: 0;
        transform: translateY(0);
    }
}

/* For desktop: slide down transition */
@media (min-width: 1024px) {
    .slide-down-enter,
    .slide-down-leave-to {
        transform: translateY(-100%);
        opacity: 0;
    }
}

.dispatch-banner {
    position: absolute;
    top: 70px;
    left: 100px;
    transform: translateX(-50%);
    padding: 1rem 1.5rem;
    background: #ffffff;
    border: 1px solid #273276;
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    z-index: 1200;
}

.banner-text {
    flex: 1;
    font-size: 1.4rem;
    line-height: 1.4;
    color: #333;
}

.banner-actions {
    display: flex;
    gap: 0.75rem;
}

.btn {
    font-size: 1.3rem;
    padding: 0.6rem 1rem;
    border-radius: 0.375rem;
    cursor: pointer;
    border: none;
    transition: background-color 0.2s;
}

.btn-change {
    background: #0073e6;
    color: #fff;
}

.btn-change:hover {
    background: #005bb5;
}

.btn-continue {
    background: transparent;
    color: #0073e6;
}

.btn-continue:hover {
    background: rgba(0, 115, 230, 0.1);
}

.overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.modal-dialog {
    max-width: 480px;
    margin: 0 1rem;
}

.modal-content {
    background: #fff;
    border-radius: 0.75rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.modal-header {
    padding: 1rem 1.5rem;
    position: relative;
    background-color: #13193b;
    color: white;
}

.modal-title {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 600;
}

.close-icon {
    position: absolute;
    top: 0.5rem;
    right: 1.5rem;
    background: none;
    border: none;
    font-size: 2rem;
    cursor: pointer;
    color: white;
}

.modal-body {
    padding: 1rem 1.5rem;
}

.info-text {
    font-size: 1.3rem;
    line-height: 1.2;
    color: #606060;
}

.divider {
    margin: 1.5rem 0;
    position: relative;
    color: #888;
    font-size: 0.9rem;
    text-align: center;
}

.divider span {
    font-size: 1.3rem;
    line-height: 1.2;
    color: #606060;
}

.divider::before,
.divider::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 25%;
    height: 1px;
    background: #ddd;
}

.divider::before {
    left: 0;
}

.divider::after {
    right: 0;
}

.modal-footer {
    padding: 1.5rem 1.5rem;
    border-top: none;
}

.country-button {
    vertical-align: top;
    padding: 1rem 2rem;
    margin-bottom: 0.5rem;
    background: #fff;
    border: 1px solid transparent;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1.6rem;
    color: #413d3c;
    transition: background-color 0.2s;
}

.country-button:hover {
    background-color: #f7f7f7;
}
</style>
